import { emptyStructuredContext, type StructuredContext } from './context.js'
import { DomainError, assertFound } from './errors.js'
import { findingHasProposal, resolveProposal } from './findings.js'
import { newId, nowIso } from './ids.js'
import { emptyCardIntent } from './intent.js'
import { cardIdsFor, rowIdsFor, type NormalizedBoard } from './normalized.js'
import type { CardPatch, Operation } from './operations.js'
import type { Card, CardDraft, Column, Row, Swimlane } from './schemas.js'

/**
 * A committed domain command. `forward` is what the board (and the save queue) apply;
 * `inverse` is the single undo entry. Both contain only the affected entities — never a
 * full-board snapshot.
 */
export interface CommandResult {
  label: string
  forward: Operation[]
  inverse: Operation[]
}

export type Direction = 'earlier' | 'later'

// --- ordering helpers ------------------------------------------------------

/** The id an entity currently sits before, or null when it is last. Used to restore position. */
function anchorOf(ids: readonly string[], id: string): string | null {
  const index = ids.indexOf(id)
  if (index === -1 || index === ids.length - 1) return null
  return ids[index + 1] ?? null
}

/** Destination anchor for a one-step Move earlier/later, or undefined when already at the edge. */
function stepAnchor(ids: readonly string[], id: string, direction: Direction): string | null | undefined {
  const index = ids.indexOf(id)
  if (index === -1) return undefined
  const target = direction === 'earlier' ? index - 1 : index + 1
  if (target < 0 || target >= ids.length) return undefined
  if (direction === 'earlier') return ids[target] ?? null
  return target + 1 < ids.length ? (ids[target + 1] ?? null) : null
}

function stripPosition<T extends { position: number }>(entity: T): Omit<T, 'position'> {
  const { position: _position, ...rest } = entity
  return rest
}

function cardCreateInput(card: Card): Omit<Card, 'position' | 'updatedAt'> {
  const { position: _position, updatedAt: _updatedAt, ...rest } = card
  return rest
}

/** Re-create every card of a cell in its original order (the cell is empty at restore time). */
function restoreCardsIn(state: NormalizedBoard, rowId: string, columnId: string): Operation[] {
  return cardIdsFor(state, rowId, columnId).map((cardId) => ({
    type: 'card.create' as const,
    card: cardCreateInput(state.cards[cardId]!),
    beforeId: null,
  }))
}

/**
 * Rebuild one row and its cards.
 *
 * `placement: 'anchored'` restores a single deleted row next to its surviving sibling.
 * `placement: 'append'` is for restoring a whole swimlane, where the siblings do not exist
 * yet — appending them in their original order reproduces the order without dangling anchors.
 */
function restoreRow(state: NormalizedBoard, rowId: string, placement: 'anchored' | 'append'): Operation[] {
  const row = state.rows[rowId]!
  const beforeId = placement === 'anchored' ? anchorOf(rowIdsFor(state, row.swimlaneId), rowId) : null
  const ops: Operation[] = [{ type: 'row.create', row: stripPosition<Row>(row), beforeId }]
  for (const columnId of state.columnIds) ops.push(...restoreCardsIn(state, rowId, columnId))
  return ops
}

// --- board -----------------------------------------------------------------

export function renameBoard(state: NormalizedBoard, name: string): CommandResult | null {
  const trimmed = name.trim()
  if (trimmed.length === 0) throw new DomainError('validation', 'Board name is required', { name: 'Board name is required' })
  if (trimmed === state.board.name) return null
  return {
    label: 'Rename board',
    forward: [{ type: 'board.rename', name: trimmed }],
    inverse: [{ type: 'board.rename', name: state.board.name }],
  }
}

// --- structured context ----------------------------------------------------

/** A structural owner of context. Card context is edited through `updateCard`. */
export type ContextOwner =
  | { scope: 'board' }
  | { scope: 'swimlane' | 'row' | 'column'; id: string }

const CONTEXT_OWNER_LABELS = { board: 'Board', swimlane: 'Swimlane', row: 'Row', column: 'Column' } as const

/**
 * Replaces the context one scope owns. Only that scope is written: descendants resolve the
 * change on their next read, and nothing is copied onto them.
 */
export function setScopeContext(
  state: NormalizedBoard,
  owner: ContextOwner,
  context: StructuredContext,
): CommandResult | null {
  const label = `Edit ${CONTEXT_OWNER_LABELS[owner.scope].toLowerCase()} context`
  if (owner.scope === 'board') {
    const previous = state.board.context ?? emptyStructuredContext()
    if (sameValue(previous, context)) return null
    return {
      label,
      forward: [{ type: 'board.setContext', context }],
      inverse: [{ type: 'board.setContext', context: previous }],
    }
  }
  const entities = { swimlane: state.swimlanes, row: state.rows, column: state.columns }[owner.scope]
  const entity = assertFound(entities[owner.id], CONTEXT_OWNER_LABELS[owner.scope], owner.id)
  const previous = entity.context ?? emptyStructuredContext()
  if (sameValue(previous, context)) return null
  const type = `${owner.scope}.setContext` as const
  return {
    label,
    forward: [{ type, id: owner.id, context }],
    inverse: [{ type, id: owner.id, context: previous }],
  }
}

// --- findings --------------------------------------------------------------

/**
 * Accepts an open finding. With a proposal, its change is applied to the target as an
 * ordinary edit — a card save is a new card version — together with the status change,
 * so one undo reverses both. Throws a validation error when the proposal no longer fits.
 */
export function acceptFinding(state: NormalizedBoard, id: string): CommandResult {
  const finding = assertFound(state.findings[id], 'Finding', id)
  if (finding.status !== 'open') throw new DomainError('invalid_operation', 'Only an open finding can be accepted')
  const forward: Operation[] = []
  const inverse: Operation[] = []
  if (findingHasProposal(finding)) {
    const resolved = resolveProposal(state, finding)
    let change: CommandResult | null = null
    if (finding.targetScope === 'card') {
      const draft: Partial<CardDraft> = {}
      if (resolved.context) draft.context = resolved.context
      if (resolved.relationships) draft.relationships = resolved.relationships
      change = updateCard(state, finding.targetId, draft)
    } else if (resolved.context) {
      const owner: ContextOwner =
        finding.targetScope === 'board' ? { scope: 'board' } : { scope: finding.targetScope, id: finding.targetId }
      change = setScopeContext(state, owner, resolved.context)
    }
    if (change) {
      forward.push(...change.forward)
      inverse.push(...change.inverse)
    }
  }
  forward.push({ type: 'finding.resolve', id, status: 'accepted' })
  inverse.unshift({ type: 'finding.resolve', id, status: 'open' })
  return { label: 'Accept finding', forward, inverse }
}

export function dismissFinding(state: NormalizedBoard, id: string): CommandResult {
  const finding = assertFound(state.findings[id], 'Finding', id)
  if (finding.status !== 'open') throw new DomainError('invalid_operation', 'Only an open finding can be dismissed')
  return {
    label: 'Dismiss finding',
    forward: [{ type: 'finding.resolve', id, status: 'dismissed' }],
    inverse: [{ type: 'finding.resolve', id, status: 'open' }],
  }
}

// --- column groups ---------------------------------------------------------

export function createGroup(state: NormalizedBoard, title: string): CommandResult {
  const group = { id: newId(), boardId: state.board.id, title: title.trim() }
  return {
    label: 'Add group',
    forward: [{ type: 'group.create', group }],
    inverse: [{ type: 'group.delete', id: group.id }],
  }
}

export type HeadingScope = 'group' | 'column' | 'swimlane' | 'row'

/** Notes on a group, column, swimlane or row heading. */
export function headingNotes(state: NormalizedBoard, scope: HeadingScope, id: string): string {
  const entity =
    scope === 'group' ? state.groups[id] : scope === 'column' ? state.columns[id] : scope === 'swimlane' ? state.swimlanes[id] : state.rows[id]
  return entity?.notes ?? ''
}

export function setHeadingNotes(state: NormalizedBoard, scope: HeadingScope, id: string, notes: string): CommandResult | null {
  const label = { group: 'Column group', column: 'Column', swimlane: 'Swimlane', row: 'Row' }[scope]
  const entity =
    scope === 'group' ? state.groups[id] : scope === 'column' ? state.columns[id] : scope === 'swimlane' ? state.swimlanes[id] : state.rows[id]
  assertFound(entity, label, id)
  const previous = entity?.notes ?? ''
  if (previous === notes) return null
  const op = (value: string): Operation => ({ type: `${scope}.setNotes`, id, notes: value }) as Operation
  return { label: 'Edit notes', forward: [op(notes)], inverse: [op(previous)] }
}

export function renameGroup(state: NormalizedBoard, id: string, title: string): CommandResult | null {
  const group = assertFound(state.groups[id], 'Column group', id)
  const trimmed = title.trim()
  if (trimmed === group.title) return null
  return {
    label: 'Rename group',
    forward: [{ type: 'group.rename', id, title: trimmed }],
    inverse: [{ type: 'group.rename', id, title: group.title }],
  }
}

export function deleteGroup(state: NormalizedBoard, id: string): CommandResult {
  const group = assertFound(state.groups[id], 'Column group', id)
  const members = state.columnIds.filter((columnId) => state.columns[columnId]!.groupId === id)
  return {
    label: 'Delete group',
    forward: [{ type: 'group.delete', id }],
    inverse: [
      { type: 'group.create', group },
      ...members.map((columnId) => ({ type: 'column.setGroup' as const, id: columnId, groupId: id })),
    ],
  }
}

// --- columns ---------------------------------------------------------------

export function createColumn(
  state: NormalizedBoard,
  input: { title: string; beforeId?: string | null; groupId?: string | null },
): CommandResult {
  const column = {
    id: newId(),
    boardId: state.board.id,
    title: input.title.trim(),
    groupId: input.groupId ?? null,
  }
  return {
    label: 'Add column',
    forward: [{ type: 'column.create', column, beforeId: input.beforeId ?? null }],
    inverse: [{ type: 'column.delete', id: column.id }],
  }
}

export function renameColumn(state: NormalizedBoard, id: string, title: string): CommandResult | null {
  const column = assertFound(state.columns[id], 'Column', id)
  const trimmed = title.trim()
  if (trimmed === column.title) return null
  return {
    label: 'Rename column',
    forward: [{ type: 'column.rename', id, title: trimmed }],
    inverse: [{ type: 'column.rename', id, title: column.title }],
  }
}

export function setColumnGroup(state: NormalizedBoard, id: string, groupId: string | null): CommandResult | null {
  const column = assertFound(state.columns[id], 'Column', id)
  if (column.groupId === groupId) return null
  return {
    label: 'Change column group',
    forward: [{ type: 'column.setGroup', id, groupId }],
    inverse: [{ type: 'column.setGroup', id, groupId: column.groupId }],
  }
}

export function moveColumn(state: NormalizedBoard, id: string, direction: Direction): CommandResult | null {
  assertFound(state.columns[id], 'Column', id)
  const beforeId = stepAnchor(state.columnIds, id, direction)
  if (beforeId === undefined) return null
  return {
    label: 'Move column',
    forward: [{ type: 'column.move', id, beforeId }],
    inverse: [{ type: 'column.move', id, beforeId: anchorOf(state.columnIds, id) }],
  }
}

export function deleteColumn(state: NormalizedBoard, id: string): CommandResult {
  const column = assertFound(state.columns[id], 'Column', id)
  const restore: Operation[] = [
    { type: 'column.create', column: stripPosition<Column>(column), beforeId: anchorOf(state.columnIds, id) },
  ]
  for (const swimlaneId of state.swimlaneIds) {
    for (const rowId of rowIdsFor(state, swimlaneId)) restore.push(...restoreCardsIn(state, rowId, id))
  }
  return {
    label: 'Delete column',
    forward: [{ type: 'column.delete', id }],
    inverse: restore,
  }
}

// --- swimlanes -------------------------------------------------------------

export function createSwimlane(
  state: NormalizedBoard,
  input: { title: string; beforeId?: string | null; withRow?: boolean },
): CommandResult {
  const swimlane = { id: newId(), boardId: state.board.id, title: input.title.trim() }
  const forward: Operation[] = [{ type: 'swimlane.create', swimlane, beforeId: input.beforeId ?? null }]
  const inverse: Operation[] = [{ type: 'swimlane.delete', id: swimlane.id }]
  if (input.withRow !== false) {
    forward.push({
      type: 'row.create',
      row: { id: newId(), boardId: state.board.id, swimlaneId: swimlane.id, title: 'New row' },
      beforeId: null,
    })
  }
  return { label: 'Add swimlane', forward, inverse }
}

export function renameSwimlane(state: NormalizedBoard, id: string, title: string): CommandResult | null {
  const swimlane = assertFound(state.swimlanes[id], 'Swimlane', id)
  const trimmed = title.trim()
  if (trimmed === swimlane.title) return null
  return {
    label: 'Rename swimlane',
    forward: [{ type: 'swimlane.rename', id, title: trimmed }],
    inverse: [{ type: 'swimlane.rename', id, title: swimlane.title }],
  }
}

export function moveSwimlane(state: NormalizedBoard, id: string, direction: Direction): CommandResult | null {
  assertFound(state.swimlanes[id], 'Swimlane', id)
  const beforeId = stepAnchor(state.swimlaneIds, id, direction)
  if (beforeId === undefined) return null
  return {
    label: 'Move swimlane',
    forward: [{ type: 'swimlane.move', id, beforeId }],
    inverse: [{ type: 'swimlane.move', id, beforeId: anchorOf(state.swimlaneIds, id) }],
  }
}

export function deleteSwimlane(state: NormalizedBoard, id: string): CommandResult {
  const swimlane = assertFound(state.swimlanes[id], 'Swimlane', id)
  const restore: Operation[] = [
    { type: 'swimlane.create', swimlane: stripPosition<Swimlane>(swimlane), beforeId: anchorOf(state.swimlaneIds, id) },
  ]
  for (const rowId of rowIdsFor(state, id)) restore.push(...restoreRow(state, rowId, 'append'))
  return {
    label: 'Delete swimlane',
    forward: [{ type: 'swimlane.delete', id }],
    inverse: restore,
  }
}

// --- rows ------------------------------------------------------------------

export function createRow(
  state: NormalizedBoard,
  input: { swimlaneId: string; title: string; beforeId?: string | null },
): CommandResult {
  assertFound(state.swimlanes[input.swimlaneId], 'Swimlane', input.swimlaneId)
  const row = {
    id: newId(),
    boardId: state.board.id,
    swimlaneId: input.swimlaneId,
    title: input.title.trim(),
  }
  return {
    label: 'Add row',
    forward: [{ type: 'row.create', row, beforeId: input.beforeId ?? null }],
    inverse: [{ type: 'row.delete', id: row.id }],
  }
}

export function renameRow(state: NormalizedBoard, id: string, title: string): CommandResult | null {
  const row = assertFound(state.rows[id], 'Row', id)
  const trimmed = title.trim()
  if (trimmed === row.title) return null
  return {
    label: 'Rename row',
    forward: [{ type: 'row.rename', id, title: trimmed }],
    inverse: [{ type: 'row.rename', id, title: row.title }],
  }
}

export function moveRow(state: NormalizedBoard, id: string, direction: Direction): CommandResult | null {
  const row = assertFound(state.rows[id], 'Row', id)
  const siblings = rowIdsFor(state, row.swimlaneId)
  const beforeId = stepAnchor(siblings, id, direction)
  if (beforeId === undefined) return null
  return {
    label: 'Move row',
    forward: [{ type: 'row.move', id, swimlaneId: row.swimlaneId, beforeId }],
    inverse: [{ type: 'row.move', id, swimlaneId: row.swimlaneId, beforeId: anchorOf(siblings, id) }],
  }
}

export function moveRowToSwimlane(state: NormalizedBoard, id: string, swimlaneId: string): CommandResult | null {
  const row = assertFound(state.rows[id], 'Row', id)
  assertFound(state.swimlanes[swimlaneId], 'Swimlane', swimlaneId)
  if (row.swimlaneId === swimlaneId) return null
  return {
    label: 'Move row to swimlane',
    forward: [{ type: 'row.move', id, swimlaneId, beforeId: null }],
    inverse: [
      { type: 'row.move', id, swimlaneId: row.swimlaneId, beforeId: anchorOf(rowIdsFor(state, row.swimlaneId), id) },
    ],
  }
}

export function deleteRow(state: NormalizedBoard, id: string): CommandResult {
  assertFound(state.rows[id], 'Row', id)
  return {
    label: 'Delete row',
    forward: [{ type: 'row.delete', id }],
    inverse: restoreRow(state, id, 'anchored'),
  }
}

// --- cards -----------------------------------------------------------------

export const EMPTY_CARD_DRAFT: CardDraft = {
  title: '',
  description: '',
  type: null,
  status: 'new',
  priority: null,
  moscow: null,
  owner: '',
  tags: [],
  colour: 'default',
  icon: null,
  estimateMinutes: null,
  dueDate: null,
  release: '',
  intent: emptyCardIntent(),
}

export function createCard(
  state: NormalizedBoard,
  input: { rowId: string; columnId: string; draft?: Partial<CardDraft>; beforeId?: string | null },
): CommandResult {
  assertFound(state.rows[input.rowId], 'Row', input.rowId)
  assertFound(state.columns[input.columnId], 'Column', input.columnId)
  const id = newId()
  const card = {
    id,
    boardId: state.board.id,
    rowId: input.rowId,
    columnId: input.columnId,
    ...EMPTY_CARD_DRAFT,
    ...input.draft,
    createdAt: nowIso(),
  }
  if (card.title.trim().length === 0) {
    throw new DomainError('validation', 'Title is required', { title: 'Title is required' })
  }
  card.title = card.title.trim()
  return {
    label: 'Add card',
    forward: [{ type: 'card.create', card, beforeId: input.beforeId ?? null }],
    inverse: [{ type: 'card.delete', id }],
  }
}

const DRAFT_FIELDS = [
  'title',
  'description',
  'type',
  'status',
  'priority',
  'moscow',
  'owner',
  'tags',
  'colour',
  'icon',
  'estimateMinutes',
  'dueDate',
  'release',
  'intent',
  'context',
  'relationships',
] as const

/** Empty values for the optional card fields, used when undoing their first write. */
const ABSENT_DEFAULTS = {
  context: () => emptyStructuredContext(),
  relationships: () => [],
} as const

/**
 * Structural comparison. Card intent is a nested object, so a reference check would make
 * every editor Save look like a change and push an empty undo entry.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((value, index) => sameValue(value, b[index]))
  }
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    const left = a as Record<string, unknown>
    const right = b as Record<string, unknown>
    const keys = new Set([...Object.keys(left), ...Object.keys(right)])
    for (const key of keys) if (!sameValue(left[key], right[key])) return false
    return true
  }
  return false
}

/** Diffs a committed editor draft against the card; returns null when nothing changed. */
export function updateCard(state: NormalizedBoard, id: string, draft: Partial<CardDraft>): CommandResult | null {
  const card = assertFound(state.cards[id], 'Card', id)
  const patch: CardPatch = {}
  const revert: CardPatch = {}
  for (const field of DRAFT_FIELDS) {
    if (!(field in draft)) continue
    const nextValue = draft[field]
    if (nextValue === undefined || sameValue(nextValue, card[field])) continue
    Object.assign(patch, { [field]: nextValue })
    // A field the card never had reverts to its explicit empty value: an absent key would
    // be dropped on the wire, and the undo would silently not reach the server.
    Object.assign(revert, { [field]: card[field] ?? ABSENT_DEFAULTS[field as keyof typeof ABSENT_DEFAULTS]?.() })
  }
  if (Object.keys(patch).length === 0) return null
  if (patch.title !== undefined && patch.title.trim().length === 0) {
    throw new DomainError('validation', 'Title is required', { title: 'Title is required' })
  }
  return {
    label: 'Edit card',
    forward: [{ type: 'card.update', id, patch }],
    inverse: [{ type: 'card.update', id, patch: revert }],
  }
}

export function deleteCard(state: NormalizedBoard, id: string): CommandResult {
  const card = assertFound(state.cards[id], 'Card', id)
  const siblings = cardIdsFor(state, card.rowId, card.columnId)
  return {
    label: 'Delete card',
    forward: [{ type: 'card.delete', id }],
    inverse: [{ type: 'card.create', card: cardCreateInput(card), beforeId: anchorOf(siblings, id) }],
  }
}

export function moveCard(
  state: NormalizedBoard,
  id: string,
  destination: { rowId: string; columnId: string; beforeId: string | null },
): CommandResult | null {
  const card = assertFound(state.cards[id], 'Card', id)
  assertFound(state.rows[destination.rowId], 'Row', destination.rowId)
  assertFound(state.columns[destination.columnId], 'Column', destination.columnId)

  const siblings = cardIdsFor(state, card.rowId, card.columnId)
  const sameCell = card.rowId === destination.rowId && card.columnId === destination.columnId
  if (sameCell && anchorOf(siblings, id) === destination.beforeId) return null

  return {
    label: 'Move card',
    forward: [{ type: 'card.move', id, ...destination }],
    inverse: [
      {
        type: 'card.move',
        id,
        rowId: card.rowId,
        columnId: card.columnId,
        beforeId: anchorOf(siblings, id),
      },
    ],
  }
}

/** Translate a drag or Move-dialog destination index into a `beforeId` anchor. */
export function anchorForIndex(
  state: NormalizedBoard,
  rowId: string,
  columnId: string,
  index: number,
  movingCardId?: string,
): string | null {
  const ids = cardIdsFor(state, rowId, columnId).filter((id) => id !== movingCardId)
  return index >= ids.length ? null : (ids[index] ?? null)
}
